/**
 * A /query/v2 answer: the whole text, every claim marked, and a banner that cannot be
 * scrolled past while anything in it is not verified (review C1; "To Rely or Not to
 * Rely", CHI'25: a persistent notice did best).
 *
 * The banner sits ABOVE the text, so it is read before the answer, and it stays for as
 * long as the answer is on screen. It shows whenever the answer's `verification` is not
 * `full` (the server's value, lowered when a claim could not be placed here), and counts
 * from the faithfulness report. When no annotations reached the client, or the API says
 * nothing was checked, the banner says the answer has not been checked — never silence,
 * because silence is what a verified answer looks like.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { FallbackReason } from "@/lib/api-types.ts";
import { v2Status, type V2Annotation, type V2Claim, type V2Mark } from "@/lib/queryV2.ts";
import { AnswerMarkdown } from "./AnswerMarkdown.tsx";
import { Panel } from "./primitives.tsx";
import { v2FootText, v2MarkLabeller, v2RefusalCopy } from "./v2Labels.ts";

export function V2AnswerBody({
  response,
  annotation,
  marks,
  unplaced,
  litPassage,
  onPassageClick,
  references,
}: {
  response: {
    answer: string;
    fallback_used: boolean;
    fallback_reason: FallbackReason | null;
  };
  annotation: V2Annotation;
  marks: V2Mark[];
  unplaced: V2Claim[];
  litPassage: number | null;
  onPassageClick: (passage: number | null) => void;
  references?: Map<string, number>;
}) {
  const { t } = useTranslation();
  const label = useMemo(() => v2MarkLabeller(t), [t]);
  const status = v2Status(annotation, unplaced);
  const refused = response.fallback_used;
  const refusal =
    refused && response.fallback_reason ? v2RefusalCopy(t, response.fallback_reason) : null;

  return (
    <>
      {refusal ? (
        <Panel tone="amber" title={refusal.title}>
          {refusal.body}
        </Panel>
      ) : null}

      {status.kind === "unchecked" ? (
        <div className="v2-banner v2-banner-amber" role="status">
          <strong>{t("answerV2.bannerUnchecked")}</strong>
          {refused ? null : <span> {t("answerV2.bannerUncheckedBody")}</span>}
        </div>
      ) : !refused && status.verification !== "full" ? (
        <V2Banner status={status} />
      ) : null}

      <div className={status.kind === "unchecked" ? "v2-unchecked" : undefined}>
        <AnswerMarkdown
          litPassage={litPassage}
          onPassageClick={onPassageClick}
          references={references}
          v2Marks={marks}
          v2Label={label}
        >
          {response.answer}
        </AnswerMarkdown>
      </div>
    </>
  );
}

function V2Banner({ status }: { status: Extract<ReturnType<typeof v2Status>, { kind: "checked" }> }) {
  const { t } = useTranslation();
  const amber = status.notVerified > 0 || status.unlocated > 0;
  const parts: Array<{ key: string; text: string; strong?: boolean }> = [];
  if (status.notVerified > 0) {
    parts.push({ key: "nv", text: t("answerV2.bannerNotVerified", { count: status.notVerified }), strong: true });
  }
  if (status.unlocated > 0) parts.push({ key: "ul", text: t("answerV2.bannerUnlocated"), strong: true });
  if (status.fromConversation > 0) {
    parts.push({ key: "fc", text: t("answerV2.bannerFromConversation", { count: status.fromConversation }) });
  }
  if (parts.length === 0) {
    parts.push({ key: "nc", text: t(status.total === 0 ? "answerV2.bannerNoClaims" : "answerV2.bannerNotFull") });
  }
  return (
    <div className={amber ? "v2-banner v2-banner-amber" : "v2-banner v2-banner-slate"} role="status">
      {parts.map((part, index) => (
        <span key={part.key}>
          {index > 0 ? " " : null}
          {part.strong ? <strong>{part.text}</strong> : part.text}
        </span>
      ))}
    </div>
  );
}

export function V2FootNote({ annotation, unplaced }: { annotation: V2Annotation; unplaced: V2Claim[] }) {
  const { t } = useTranslation();
  const status = v2Status(annotation, unplaced);
  // The number, never a "verified" pill: 4 of 5 is not "verified".
  return (
    <span className={status.kind === "unchecked" ? "foot-note faint" : "foot-note"}>
      {v2FootText(t, status)}
    </span>
  );
}
